import React from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel
} from '@mui/material';
import { Trip } from '@/types';
import { ParticipantData } from '@/types/signup';
import MemberStatusChip from '@/components/ui/MemberStatusChip';
import { getSkiPassPriceAtTripStart, calculateAgeAtDate } from '@/utils/calculations';
import { useI18n } from '@/hooks/useI18n';

interface SkiPreferencesSectionProps {
  signUpType: 'individual' | 'group';
  primaryParticipant: ParticipantData;
  trip: Trip;
  onPrimaryParticipantChange: (field: keyof ParticipantData, value: string | boolean | undefined) => void;
}

const SkiPreferencesSection: React.FC<SkiPreferencesSectionProps> = ({
  signUpType,
  primaryParticipant,
  trip,
  onPrimaryParticipantChange
}) => {
  const { t, formatCurrency } = useI18n();
  
  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        ⛷️ {signUpType === 'group' ? t('signup.primaryParticipantSkiPreferences') : t('signup.skiPreferences')}
      </Typography>

      {/* Ski Club Membership */}
      <Box sx={{ mb: 3 }}>
        <FormControlLabel
          control={
            <Switch
              checked={primaryParticipant.isSkiClubMember}
              onChange={(e) => onPrimaryParticipantChange('isSkiClubMember', e.target.checked)}
            />
          }
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              🏅 {t('participant.isSkiClubMember')}
              <MemberStatusChip isMember={primaryParticipant.isSkiClubMember} />
            </Box>
          }
        />
      </Box>

      {/* Ski Pass Selection */}
      <Box sx={{ mt: 3 }}>
        <FormControl fullWidth>
          <InputLabel>🎿 {t('participant.preferredSkiPass')}</InputLabel>
          <Select
            value={primaryParticipant.preferredSkiPass || 'none'}
            onChange={(e) => onPrimaryParticipantChange('preferredSkiPass', e.target.value === 'none' ? undefined : e.target.value)}
            label={`🎿 ${t('participant.preferredSkiPass')}`}
          >
            <MenuItem value="none">{t('skipass.noSkiPass')}</MenuItem>
            {trip.skiPasses && trip.skiPasses.length > 0 ? (
              trip.skiPasses.map((skiPass) => {
                const ageAtTripStart = calculateAgeAtDate(primaryParticipant.dateOfBirth, trip.startDate);
                const price = getSkiPassPriceAtTripStart(primaryParticipant.dateOfBirth, trip.startDate, trip.skiPassAgeLevels, skiPass);
                return (
                  <MenuItem key={skiPass.id} value={skiPass.id}>
                    {skiPass.name} - {formatCurrency(price)} {ageAtTripStart > 0 && `(${t('participant.age')}: ${ageAtTripStart})`}
                  </MenuItem>
                );
              })
            ) : (
              <MenuItem value="no-passes-available" disabled>
                {t('skipass.noSkiPassesConfigured')}
              </MenuItem>
            )}
          </Select>
        </FormControl>
      </Box>
    </Box>
  );
};

export default SkiPreferencesSection;
